import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import '../styles/CalendarView.css';
import { useModal } from '../context/ModalContext';
import { useOrgBrand } from '../context/OrgBrandContext';

const localizer = momentLocalizer(moment);

// Same "signature" used to tell whether a Google Calendar event IS a Velvet
// meeting reflected back from Google (which it will be, since scheduling a
// Velvet meeting creates a real event on the organizer's calendar) so it
// isn't shown twice - once as a Velvet meeting and again as a bare calendar
// event with none of the Velvet context.
const eventSignature = (title, start) => `${(title || '').trim().toLowerCase()}|${start.getTime()}`;

const LiveKitCalendarView = () => {
  const modal = useModal();
  const { selectedOrganization, selectedBrand } = useOrgBrand();
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());

  const orgId = selectedOrganization?.id;
  const brandId = selectedBrand?.id;

  const fetchVelvetMeetings = async () => {
    const response = await axios.get('/api/livekit/meetings', {
      params: {
        organization_id: orgId,
        brand_id: brandId
      },
      withCredentials: true
    });

    const meetings = response.data.meetings || [];
    const now = new Date();

    return meetings
      .filter(meeting => meeting.scheduled_start)
      .map(meeting => {
        const start = new Date(meeting.scheduled_start);
        const end = meeting.scheduled_end
          ? new Date(meeting.scheduled_end)
          : new Date(start.getTime() + 60 * 60 * 1000);

        return {
          id: `velvet-${meeting.meeting_id}`,
          title: meeting.title || 'Untitled Meeting',
          start,
          end,
          description: meeting.description || '',
          status: meeting.room_status,
          source: 'velvet',
          isPast: end < now,
        };
      });
  };

  // Pulls in the organizer's actual Google/Microsoft calendar so this view -
  // and the availability check when scheduling - reflect real conflicts, not
  // just meetings scheduled through Velvet. Failing to load this (no
  // calendar connected, needs reauth, etc.) shouldn't block Velvet meetings
  // from showing, so failures here are swallowed rather than surfaced as the
  // page-level error.
  const fetchExternalCalendarEvents = async () => {
    try {
      const now = new Date();
      const params = new URLSearchParams({
        timeMin: new Date(now.getFullYear(), now.getMonth(), 1).toISOString(),
        timeMax: new Date(now.getFullYear(), now.getMonth() + 3, 0).toISOString(),
        maxResults: 2500
      });

      const response = await fetch(`/api/calendar/events?${params}`, {
        method: 'GET',
        credentials: 'include'
      });

      if (!response.ok) return [];
      const data = await response.json();
      if (!data.events) return [];

      return data.events
        .filter(event => {
          if (event.eventType === 'workingLocation' || event.eventType === 'outOfOffice') return false;
          if (!event.start) return false;
          return true;
        })
        .map(event => {
          const start = new Date(event.start.dateTime || event.start.date);
          const end = new Date(event.end.dateTime || event.end.date);
          return {
            id: `google-${event.id}`,
            title: event.summary || 'Untitled Event',
            start,
            end,
            description: event.description || '',
            source: 'google',
            isPast: end < now,
          };
        });
    } catch (err) {
      console.error('Error fetching external calendar events:', err);
      return [];
    }
  };

  const fetchAll = useCallback(async () => {
    try {
      setIsLoading(true);

      const [velvetEvents, externalEvents] = await Promise.all([
        fetchVelvetMeetings(),
        fetchExternalCalendarEvents()
      ]);

      const velvetSignatures = new Set(velvetEvents.map(e => eventSignature(e.title, e.start)));
      const dedupedExternal = externalEvents.filter(e => !velvetSignatures.has(eventSignature(e.title, e.start)));

      setEvents([...velvetEvents, ...dedupedExternal]);
      setError(null);
    } catch (err) {
      console.error('Error fetching LiveKit meetings:', err);
      if (err.response?.status === 403) {
        setError('Access denied. You do not have permission to view meetings.');
      } else {
        setError(err.response?.data?.error || 'Failed to load meetings');
      }
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, brandId]);

  useEffect(() => {
    // Agency context is resolved server-side from the session (same as every
    // other LiveKit endpoint) - don't gate the fetch on the local
    // activeAgency state, which can be null even when the session already
    // has a valid agency (e.g. after a refresh), silently leaving this
    // calendar empty with no error shown.
    fetchAll();
  }, [fetchAll]);

  const handleSelectEvent = useCallback((event) => {
    const details = [
      event.description,
      event.source === 'velvet' ? `Status: ${event.status || 'scheduled'}` : null,
      `${moment(event.start).format('MMM D, YYYY h:mma')} - ${moment(event.end).format('h:mma')}`
    ].filter(Boolean).join('\n\n');

    modal.alert(details, event.title);
  }, [modal]);

  const eventStyleGetter = (event) => {
    let backgroundColor = event.source === 'velvet' ? '#1a73e8' : '#70757a';
    let opacity = 1;

    if (event.isPast) {
      backgroundColor = '#9e9e9e';
      opacity = 0.8;
    }

    return {
      style: {
        backgroundColor,
        borderRadius: '4px',
        opacity,
        color: 'white',
        border: '0px',
        display: 'block',
        fontSize: '12px',
        padding: '2px 4px'
      }
    };
  };

  if (error) {
    return (
      <div className="calendar-error">
        <h3>Unable to load meetings</h3>
        <p>{error}</p>
        <button onClick={() => fetchAll()} className="retry-btn">
          Retry
        </button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="calendar-loading">
        <div className="loading-spinner"></div>
        <p>Loading your Velvet Meetings...</p>
      </div>
    );
  }

  const velvetCount = events.filter(e => e.source === 'velvet').length;
  const externalCount = events.filter(e => e.source === 'google').length;

  return (
    <div className="calendar-content">
      <div className="calendar-header">
        <div className="calendar-header-info">
          <h2>Velvet Meetings Calendar</h2>
          <p>
            Showing {velvetCount} Velvet meeting{velvetCount === 1 ? '' : 's'}
            {externalCount > 0 ? ` and ${externalCount} other calendar event${externalCount === 1 ? '' : 's'}` : ''}
          </p>
        </div>
        <div className="calendar-actions">
          <button onClick={() => fetchAll()} className="refresh-btn">
            Refresh Calendar
          </button>
        </div>
      </div>

      <div className="calendar-view-wrapper">
        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          style={{ height: '100%' }}
          onSelectEvent={handleSelectEvent}
          views={['month']}
          defaultView="month"
          date={currentDate}
          onNavigate={setCurrentDate}
          popup
          showMultiDayTimes={false}
          tooltipAccessor={(event) => event.description || event.title}
          eventPropGetter={eventStyleGetter}
          dayLayoutAlgorithm='no-overlap'
          formats={{
            eventTimeRangeFormat: ({ start, end }, culture, localizer) =>
              localizer.format(start, 'h:mma', culture) + ' - ' + localizer.format(end, 'h:mma', culture),
            timeGutterFormat: (date, culture, localizer) =>
              localizer.format(date, 'ha', culture),
            monthHeaderFormat: (date, culture, localizer) =>
              localizer.format(date, 'MMMM YYYY', culture),
          }}
        />
      </div>
    </div>
  );
};

export default LiveKitCalendarView;
